import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SERVICES } from '../data/mockData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import heroImage from '../assets/images/Heroimage.jpg';
import { useLanguage } from '../context/LanguageContext';

const { FiArrowRight, FiCheckCircle } = FiIcons;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const Home = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[100dvh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt={t('home.heroAlt')} 
            className="w-full h-full object-cover opacity-30 md:opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 md:via-primary/80 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="max-w-3xl">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl leading-tight text-text-dark mb-6">
              {t('home.heroTitleLine1')} <br className="hidden sm:block"/>
              <span className="italic text-accent">{t('home.heroTitleAccent')}</span> {t('home.heroTitleLine2')}
            </h1>
            <p className="text-base sm:text-lg text-text-light mb-10 max-w-lg leading-relaxed">
              {t('home.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="px-8 py-4 bg-text-dark text-white text-center uppercase tracking-widest text-sm hover:bg-black transition-colors shadow-lg">
                {t('nav.bookAppointment')}
              </Link>
              <Link to="/courses" className="px-8 py-4 border border-text-dark text-center uppercase tracking-widest text-sm hover:bg-text-dark hover:text-white transition-colors">
                {t('home.exploreCourses')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }} 
            variants={fadeInUp} 
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-center sm:divide-x divide-text-dark/10"
          >
            <div className="py-4">
              <h3 className="font-serif text-3xl md:text-4xl mb-2">8+</h3>
              <p className="uppercase tracking-widest text-xs text-text-light">{t('home.yearsExperience')}</p>
            </div>
            <div className="py-4">
              <h3 className="font-serif text-3xl md:text-4xl mb-2">1000+</h3>
              <p className="uppercase tracking-widest text-xs text-text-light">{t('home.happyClients')}</p>
            </div>
            <div className="py-4 sm:col-span-2 md:col-span-1">
              <h3 className="font-serif text-3xl md:text-4xl mb-2">5★</h3>
              <p className="uppercase tracking-widest text-xs text-text-light">{t('home.ratedSalon')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-primary">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <h2 className="font-serif text-3xl md:text-4xl mb-4">{t('home.signatureServices')}</h2>
              <p className="text-text-light max-w-md">{t('home.signatureServicesDescription')}</p>
            </motion.div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-6 py-2 border border-text-dark hover:bg-text-dark hover:text-white transition-all text-sm uppercase tracking-widest font-medium"
            >
              {t('home.viewAll')} <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
            {SERVICES.slice(0, 3).map((service, index) => (
              <motion.div 
                key={service.id} 
                initial="hidden" 
                whileInView="visible" 
                viewport={{ once: true }} 
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { delay: index * 0.1 } }
                }} 
                className="group"
              >
                <div className="overflow-hidden mb-6 aspect-[4/5] sm:aspect-square md:aspect-[4/5]">
                  <img src={service.image} alt={service.title[language]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <h3 className="font-serif text-2xl mb-2">{service.title[language]}</h3>
                <p className="text-text-light text-sm mb-4 line-clamp-2">{service.description[language]}</p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-2 border border-text-dark hover:bg-text-dark hover:text-white transition-all text-sm uppercase tracking-widest font-medium"
                >
                  {t('home.bookService')}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses CTA - Reduced padding-bottom to touch footer better if needed */}
      <section className="pt-24 pb-20 bg-text-dark text-white relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <h2 className="font-serif text-3xl md:text-5xl mb-6">{t('home.ctaTitle')}</h2>
              <p className="text-white/80 mb-8 leading-relaxed max-w-lg">
                {t('home.ctaDescription')}
              </p>
              <ul className="space-y-4 mb-10">
                {t('home.ctaBullets').map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs sm:text-sm uppercase tracking-widest font-medium">
                    <SafeIcon icon={FiCheckCircle} className="text-accent flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <Link to="/courses" className="inline-block w-full sm:w-auto px-8 py-4 bg-white text-text-dark text-center uppercase tracking-widest text-sm font-bold hover:bg-accent hover:text-white transition-all">
                {t('home.exploreNailCourses')}
              </Link>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              whileInView={{ opacity: 1, scale: 1 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.8 }} 
              className="relative h-[400px] sm:h-[500px] lg:h-[600px] hidden sm:block"
            >
              <img src="https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?auto=format&fit=crop&q=80&w=800" alt={t('home.coursesAlt')} className="w-full h-full object-cover shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
